import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { deleteAssistantThread, getAssistantThreads } from '@/data/client';
import { AssistantPanel } from '@/components/assistant/AssistantPanel';

/* The full page: past conversations on the left, the open one on the right.
   Same panel as the drawer, with room for charts and long threads. */
export function AssistantPage() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const threadId = params.get('thread');
  const { data } = useQuery({ queryKey: ['assistant', 'threads'], queryFn: getAssistantThreads, staleTime: 30_000 });
  const threads = data?.threads ?? [];
  const select = (id: string | null) => setParams(id ? { thread: id } : {}, { replace: true });

  return (
    <div className="asst-page">
      <Card padding="lg" className="asst-page__list">
        <div className="asst-page__listhead">
          <h2>Conversations</h2>
          <Button size="sm" variant="outline" onClick={() => select(null)} iconLeft={<Icon name="plus" size={14} />}>New</Button>
        </div>
        {threads.length === 0 ? <p className="dash-muted">Nothing yet. Ask your first question on the right.</p> : (
          <ul className="asst-page__threads">
            {threads.map((t) => (
              <li key={t.id} className={t.id === threadId ? 'is-active' : ''}>
                <button type="button" className="asst-page__thread" onClick={() => select(t.id)}>
                  <span className="asst-page__thread-title">{t.title || 'Conversation'}</span>
                  <span className="asst-page__thread-meta">{t.updatedAt.slice(0, 10)} · {Math.floor(t.messageCount / 2)} {t.messageCount >= 4 ? 'questions' : 'question'}</span>
                </button>
                <button type="button" className="asst-page__thread-del" aria-label="Delete conversation" onClick={async () => {
                  if (!window.confirm('Delete this conversation?')) return;
                  await deleteAssistantThread(t.id);
                  if (t.id === threadId) select(null);
                  qc.invalidateQueries({ queryKey: ['assistant', 'threads'] });
                }}><Icon name="trash" size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card padding="lg" className="asst-page__main">
        <AssistantPanel threadId={threadId} onThread={(id) => select(id)} />
      </Card>
    </div>
  );
}
